
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { learningAPI } from '../services/learningApi';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface ChatSession {
  id: string;
  title: string;
  messages: ChatMessage[];
  createdAt: Date;
  updatedAt: Date;
}

interface StudentInfo {
  major: string;
  learningStyle: string;
  learningGoals: string;
  interests: string[];
  yearOfStudy: string;
  preferredTopics: string;
  studyHoursPerWeek: number;
  academicBackground: string;
}

const LearningPage: React.FC = () => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Student information form state
  const [hasCompletedForm, setHasCompletedForm] = useState(false);
  const [studentInfo, setStudentInfo] = useState<StudentInfo>({
    major: '',
    learningStyle: '',
    learningGoals: '',
    interests: [],
    yearOfStudy: '',
    preferredTopics: '',
    studyHoursPerWeek: 10,
    academicBackground: ''
  });

  useEffect(() => {
    // Check if student has completed the information form
    const savedStudentInfo = localStorage.getItem(`student_info_${user?.id}`);
    if (savedStudentInfo) {
      try {
        setStudentInfo(JSON.parse(savedStudentInfo));
        setHasCompletedForm(true);
      } catch (error) {
        console.error('Error loading student info:', error);
      }
    }
    
    // Load sessions from localStorage
    const savedSessions = localStorage.getItem(`learning_sessions_${user?.id}`);
    if (savedSessions) {
      try {
        const parsedSessions = JSON.parse(savedSessions);
        setSessions(parsedSessions.map((s: any) => ({
          ...s,
          createdAt: new Date(s.createdAt),
          updatedAt: new Date(s.updatedAt),
          messages: s.messages.map((m: any) => ({
            ...m,
            timestamp: new Date(m.timestamp)
          }))
        })));
      } catch (error) {
        console.error('Error loading sessions:', error);
      }
    }
  }, [user]);

  useEffect(() => {
    // Save sessions to localStorage
    if (sessions.length > 0 && user) {
      localStorage.setItem(`learning_sessions_${user.id}`, JSON.stringify(sessions));
    }
  }, [sessions, user]);

  useEffect(() => {
    scrollToBottom();
  }, [currentSession?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const createNewSession = () => {
    const newSession: ChatSession = {
      id: `session_${Date.now()}`,
      title: 'New Conversation',
      messages: [],
      createdAt: new Date(),
      updatedAt: new Date()
    };
    setSessions([newSession, ...sessions]);
    setCurrentSession(newSession);
  };

  const selectSession = (session: ChatSession) => {
    setCurrentSession(session);
  };

  const deleteSession = (sessionId: string) => {
    setSessions(sessions.filter(s => s.id !== sessionId));
    if (currentSession?.id === sessionId) {
      setCurrentSession(null);
    }
  };

  const renameSession = (sessionId: string, newTitle: string) => {
    setSessions(sessions.map(s => 
      s.id === sessionId ? { ...s, title: newTitle } : s
    ));
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading || !currentSession) return;

    const userMessage: ChatMessage = {
      id: `msg_${Date.now()}`,
      role: 'user',
      content: inputMessage.trim(),
      timestamp: new Date()
    };

    // Update current session with user message
    const updatedSession = {
      ...currentSession,
      messages: [...currentSession.messages, userMessage],
      updatedAt: new Date()
    };

    // Auto-generate title from first message
    if (currentSession.messages.length === 0) {
      updatedSession.title = inputMessage.trim().slice(0, 50) + (inputMessage.length > 50 ? '...' : '');
    }

    setCurrentSession(updatedSession);
    setSessions(sessions.map(s => s.id === currentSession.id ? updatedSession : s));
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await learningAPI.chat(inputMessage, currentSession.messages, studentInfo);

      const assistantMessage: ChatMessage = {
        id: `msg_${Date.now()}_assistant`,
        role: 'assistant',
        content: response.answer,
        timestamp: new Date()
      };

      const finalSession = {
        ...updatedSession,
        messages: [...updatedSession.messages, assistantMessage],
        updatedAt: new Date()
      };

      setCurrentSession(finalSession);
      setSessions(sessions.map(s => s.id === currentSession.id ? finalSession : s));
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: ChatMessage = {
        id: `msg_${Date.now()}_error`,
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      };

      const finalSession = {
        ...updatedSession,
        messages: [...updatedSession.messages, errorMessage],
        updatedAt: new Date()
      };

      setCurrentSession(finalSession);
      setSessions(sessions.map(s => s.id === currentSession.id ? finalSession : s));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInterestToggle = (interest: string) => {
    setStudentInfo(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Save student information to localStorage
    localStorage.setItem(`student_info_${user?.id}`, JSON.stringify(studentInfo));
    setHasCompletedForm(true);
  };

  const handleUpdateProfile = () => {
    setHasCompletedForm(false);
  };

  const interestOptions = [
    'Artificial Intelligence',
    'Machine Learning',
    'Data Science',
    'Software Engineering',
    'Cybersecurity',
    'Web Development',
    'Mobile Development',
    'Cloud Computing',
    'Database Systems',
    'Computer Vision',
    'Natural Language Processing',
    'Robotics'
  ];

  // If form not completed, show the student information form
  if (!hasCompletedForm) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-gray-50 via-blue-50 to-gray-100 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-[#C41230] to-red-700 rounded-2xl shadow-lg mb-4">
                <span className="text-4xl">📚</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                Welcome to Personalized Learning!
              </h1>
              <p className="text-gray-600">
                Help us understand your learning needs better by completing this profile
              </p>
            </div>

            <form onSubmit={handleFormSubmit} className="space-y-6">
              {/* Major Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Major / Program <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={studentInfo.major}
                  onChange={(e) => setStudentInfo({ ...studentInfo, major: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C41230] focus:border-transparent outline-none"
                >
                  <option value="">Select your major</option>
                  <option value="MSEAI">MS in Engineering - Artificial Intelligence (MSEAI)</option>
                  <option value="MSIT">MS in Information Technology (MSIT)</option>
                  <option value="MSECE">MS in Electrical and Computer Engineering (MSECE)</option>
                </select>
              </div>

              {/* Year of Study */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Year of Study <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={studentInfo.yearOfStudy}
                  onChange={(e) => setStudentInfo({ ...studentInfo, yearOfStudy: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C41230] focus:border-transparent outline-none"
                >
                  <option value="">Select your year</option>
                  <option value="1st">1st Year</option>
                  <option value="2nd">2nd Year</option>
                  <option value="3rd">3rd Year</option>
                  <option value="4th">4th Year</option>
                </select>
              </div>

              {/* Learning Style */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Learning Style <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={studentInfo.learningStyle}
                  onChange={(e) => setStudentInfo({ ...studentInfo, learningStyle: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C41230] focus:border-transparent outline-none"
                >
                  <option value="">Select your learning style</option>
                  <option value="visual">Visual (prefer diagrams, charts, videos)</option>
                  <option value="auditory">Auditory (prefer lectures, discussions)</option>
                  <option value="reading">Reading/Writing (prefer textbooks, notes)</option>
                  <option value="kinesthetic">Kinesthetic (prefer hands-on, practical)</option>
                  <option value="mixed">Mixed (combination of styles)</option>
                </select>
              </div>

              {/* Study Hours Per Week */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Available Study Hours Per Week: {studentInfo.studyHoursPerWeek} hours
                </label>
                <input
                  type="range"
                  min="5"
                  max="30"
                  value={studentInfo.studyHoursPerWeek}
                  onChange={(e) => setStudentInfo({ ...studentInfo, studyHoursPerWeek: parseInt(e.target.value) })}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>5 hrs</span>
                  <span>30 hrs</span>
                </div>
              </div>

              {/* Interests */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Areas of Interest <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {interestOptions.map((interest) => (
                    <button
                      key={interest}
                      type="button"
                      onClick={() => handleInterestToggle(interest)}
                      className={`px-3 py-2 rounded-lg text-sm transition ${
                        studentInfo.interests.includes(interest)
                          ? 'bg-[#C41230] text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {interest}
                    </button>
                  ))}
                </div>
                {studentInfo.interests.length === 0 && (
                  <p className="text-sm text-red-500 mt-1">Please select at least one interest</p>
                )}
              </div>

              {/* Learning Goals */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Learning Goals <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  value={studentInfo.learningGoals}
                  onChange={(e) => setStudentInfo({ ...studentInfo, learningGoals: e.target.value })}
                  placeholder="What do you want to achieve? (e.g., Master machine learning, Build strong programming skills, Prepare for industry roles)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C41230] focus:border-transparent outline-none"
                  rows={3}
                />
              </div>

              {/* Preferred Topics */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Preferred Learning Topics
                </label>
                <textarea
                  value={studentInfo.preferredTopics}
                  onChange={(e) => setStudentInfo({ ...studentInfo, preferredTopics: e.target.value })}
                  placeholder="Any specific topics you'd like to focus on? (e.g., Deep Learning, Web Security, Cloud Architecture)"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C41230] focus:border-transparent outline-none"
                  rows={2}
                />
              </div>

              {/* Academic Background */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Academic Background
                </label>
                <textarea
                  value={studentInfo.academicBackground}
                  onChange={(e) => setStudentInfo({ ...studentInfo, academicBackground: e.target.value })}
                  placeholder="Tell us about your previous education, courses you've taken, or relevant experience"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C41230] focus:border-transparent outline-none"
                  rows={3}
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={studentInfo.interests.length === 0}
                className="w-full bg-gradient-to-r from-[#C41230] to-red-700 text-white py-4 px-4 rounded-lg font-semibold hover:from-red-700 hover:to-[#C41230] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#C41230] disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02] active:scale-[0.98]"
              >
                Start Personalized Learning Journey 🚀
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] bg-gray-50">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-80' : 'w-0'} transition-all duration-300 bg-white border-r border-gray-200 overflow-hidden flex flex-col`}>
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-800 mb-3">Personalized Learning</h2>
          <button
            onClick={createNewSession}
            className="w-full px-4 py-3 bg-gradient-to-r from-[#C41230] to-red-700 text-white rounded-lg font-semibold hover:opacity-90 transition flex items-center justify-center space-x-2"
          >
            <span>➕</span>
            <span>New Conversation</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {sessions.length === 0 ? (
            <div className="text-center text-gray-500 mt-8">
              <p className="text-sm">No conversations yet</p>
              <p className="text-xs mt-1">Start a new one!</p>
            </div>
          ) : (
            sessions.map(session => (
              <div
                key={session.id}
                className={`p-3 rounded-lg cursor-pointer transition group ${
                  currentSession?.id === session.id
                    ? 'bg-[#C41230] text-white'
                    : 'bg-gray-50 hover:bg-gray-100'
                }`}
                onClick={() => selectSession(session)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${
                      currentSession?.id === session.id ? 'text-white' : 'text-gray-800'
                    }`}>
                      {session.title}
                    </p>
                    <p className={`text-xs mt-1 ${
                      currentSession?.id === session.id ? 'text-red-100' : 'text-gray-500'
                    }`}>
                      {session.messages.length} messages
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm('Delete this conversation?')) {
                        deleteSession(session.id);
                      }
                    }}
                    className={`ml-2 p-1 rounded hover:bg-red-100 transition ${
                      currentSession?.id === session.id ? 'text-white' : 'text-gray-400'
                    }`}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <span>👤</span>
              <span className="font-medium">{user?.name}</span>
            </div>
            <button
              onClick={handleUpdateProfile}
              className="text-xs text-[#C41230] hover:underline"
              title="Update your learning profile"
            >
              ⚙️ Profile
            </button>
          </div>
          {studentInfo.major && (
            <p className="text-xs text-gray-500 mt-1">{studentInfo.major}</p>
          )}
        </div>
      </div>

      {/* Toggle Sidebar Button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="fixed left-0 top-20 z-10 bg-[#C41230] text-white p-2 rounded-r-lg shadow-lg hover:opacity-90 transition"
      >
        {sidebarOpen ? '◀' : '▶'}
      </button>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {currentSession ? (
          <>
            {/* Chat Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-800">{currentSession.title}</h3>
              <p className="text-sm text-gray-500">Ask questions, solve problems, and learn interactively</p>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {currentSession.messages.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center max-w-md">
                    <div className="text-6xl mb-4">📚</div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">
                      Ready to Learn!
                    </h3>
                    <p className="text-gray-600 mb-6">
                      I'm your AI tutor. Ask me questions, work through problems together, or explore new concepts.
                    </p>
                    <div className="grid grid-cols-1 gap-3 text-left">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-sm font-medium text-blue-900">💡 For Problem-Solving:</p>
                        <p className="text-xs text-blue-700 mt-1">I'll guide you with hints and step-by-step reasoning</p>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="text-sm font-medium text-green-900">📖 For Concepts:</p>
                        <p className="text-xs text-green-700 mt-1">I'll explain thoroughly with examples</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                currentSession.messages.map(message => (
                  <div
                    key={message.id}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-3xl ${message.role === 'user' ? 'ml-12' : 'mr-12'}`}>
                      <div className={`flex items-start space-x-3 ${message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                          message.role === 'user' ? 'bg-[#C41230]' : 'bg-blue-500'
                        }`}>
                          <span className="text-white text-sm">
                            {message.role === 'user' ? '👤' : '🤖'}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className={`rounded-2xl p-4 ${
                            message.role === 'user'
                              ? 'bg-[#C41230] text-white'
                              : 'bg-white border border-gray-200 text-gray-800'
                          }`}>
                            <div className="whitespace-pre-wrap break-words">{message.content}</div>
                          </div>
                          <p className={`text-xs text-gray-500 mt-1 ${message.role === 'user' ? 'text-right' : 'text-left'}`}>
                            {message.timestamp.toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="max-w-3xl mr-12">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-blue-500">
                        <span className="text-white text-sm">🤖</span>
                      </div>
                      <div className="bg-white border border-gray-200 rounded-2xl p-4">
                        <div className="flex space-x-2">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="bg-white border-t border-gray-200 p-4">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-end space-x-3">
                  <div className="flex-1 relative">
                    <textarea
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Ask a question or describe a problem you're working on..."
                      rows={1}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C41230] focus:border-transparent outline-none resize-none"
                      style={{ minHeight: '48px', maxHeight: '120px' }}
                    />
                  </div>
                  <button
                    onClick={handleSendMessage}
                    disabled={!inputMessage.trim() || isLoading}
                    className="px-6 py-3 bg-gradient-to-r from-[#C41230] to-red-700 text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    Send
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Press Enter to send, Shift+Enter for new line
                </p>
              </div>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="text-6xl mb-4">💬</div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                Start a Conversation
              </h3>
              <p className="text-gray-600 mb-6">
                Click "New Conversation" to begin your personalized learning journey
              </p>
              <button
                onClick={createNewSession}
                className="px-6 py-3 bg-gradient-to-r from-[#C41230] to-red-700 text-white rounded-lg font-semibold hover:opacity-90 transition"
              >
                Create New Conversation
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LearningPage;
